import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

// Custom hook for counting animation
const useCountUp = (end, duration = 1000, shouldAnimate = true) => {
  const [count, setCount] = useState(shouldAnimate ? 0 : end);
  const startTimeRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!shouldAnimate) {
      setCount(end);
      return;
    }

    // Reset start time when end value changes
    startTimeRef.current = null;
    setCount(0);

    const animate = (timestamp) => {
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp;
      }

      const progress = timestamp - startTimeRef.current;
      const percentage = Math.min(progress / duration, 1);

      // Easing function for smooth animation (easeOutExpo)
      const easeOutExpo = percentage === 1 ? 1 : 1 - Math.pow(2, -10 * percentage);

      const currentCount = Math.floor(easeOutExpo * end);
      setCount(currentCount);

      if (percentage < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        setCount(end); // Ensure we end at exact value
      }
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [end, duration, shouldAnimate]);

  return count;
};

const cardStyle = {
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.08)',
  borderLeft: '3px solid #1d4ed8',
  overflow: 'hidden',
  fontFamily: "'DM Sans', sans-serif",
};

const StatsBar = ({ stats, topOffset, onHeightChange }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const containerRef = useRef(null);

  // Only animate after first render
  useEffect(() => {
    const timer = setTimeout(() => setIsFirstRender(false), 100);
    return () => clearTimeout(timer);
  }, []);

  // Track height changes
  useEffect(() => {
    if (containerRef.current && onHeightChange) {
      onHeightChange(containerRef.current.offsetHeight);
    }
  }, [isExpanded, onHeightChange]);

  const totalFlights = useCountUp(stats?.totalFlights || 0, 1000, !isFirstRender);
  const uniqueRoutes = useCountUp(stats?.uniqueRoutes || 0, 1000, !isFirstRender);
  const uniqueAircraft = useCountUp(stats?.uniqueAircraft || 0, 1000, !isFirstRender);
  const destinations = useCountUp(stats?.destinations || 0, 1000, !isFirstRender);

  if (!stats) return null;

  return (
    <div
      ref={containerRef}
      className="absolute left-4 z-50 transition-all duration-200 w-[270px]"
      style={{ top: `${topOffset}px` }}
    >
      <div style={cardStyle}>
        {/* Collapsed state */}
        {!isExpanded && (
          <button
            onClick={() => setIsExpanded(true)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 16px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
            }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f9fafb'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <span style={{ fontWeight: 500, fontSize: '14px', color: '#111111' }}>
              Statistics
            </span>
            <ChevronDown style={{ width: '16px', height: '16px', color: '#6b7280' }} />
          </button>
        )}

        {/* Expanded state */}
        {isExpanded && (
          <div style={{ padding: '16px' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
              <span style={{ fontWeight: 600, fontSize: '14px', color: '#111111' }}>
                Statistics
              </span>
              <button
                onClick={() => setIsExpanded(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <ChevronUp style={{ width: '16px', height: '16px', color: '#6b7280' }} />
              </button>
            </div>

            {/* 2x2 Stats grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {/* Total Flights */}
              <div style={{
                textAlign: 'center',
                padding: '10px 8px',
                borderRadius: '8px',
                backgroundColor: '#f9fafb',
              }}>
                <div style={{
                  fontSize: '1.45rem',
                  fontWeight: 700,
                  color: '#111111',
                  fontVariantNumeric: 'tabular-nums',
                  lineHeight: 1.1,
                }}>
                  {totalFlights}
                </div>
                <div style={{
                  fontSize: '11px',
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginTop: '4px',
                }}>
                  Total Flights
                </div>
              </div>

              {/* Unique Routes */}
              <div style={{
                textAlign: 'center',
                padding: '10px 8px',
                borderRadius: '8px',
                backgroundColor: '#f9fafb',
              }}>
                <div style={{
                  fontSize: '1.45rem',
                  fontWeight: 700,
                  color: '#111111',
                  fontVariantNumeric: 'tabular-nums',
                  lineHeight: 1.1,
                }}>
                  {uniqueRoutes}
                </div>
                <div style={{
                  fontSize: '11px',
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginTop: '4px',
                }}>
                  Unique Routes
                </div>
              </div>

              {/* Aircraft */}
              <div style={{
                textAlign: 'center',
                padding: '10px 8px',
                borderRadius: '8px',
                backgroundColor: '#f9fafb',
              }}>
                <div style={{
                  fontSize: '1.45rem',
                  fontWeight: 700,
                  color: '#111111',
                  fontVariantNumeric: 'tabular-nums',
                  lineHeight: 1.1,
                }}>
                  {uniqueAircraft}
                </div>
                <div style={{
                  fontSize: '11px',
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginTop: '4px',
                }}>
                  Aircraft
                </div>
              </div>

              {/* Destinations */}
              <div style={{
                textAlign: 'center',
                padding: '10px 8px',
                borderRadius: '8px',
                backgroundColor: '#f9fafb',
              }}>
                <div style={{
                  fontSize: '1.45rem',
                  fontWeight: 700,
                  color: '#111111',
                  fontVariantNumeric: 'tabular-nums',
                  lineHeight: 1.1,
                }}>
                  {destinations}
                </div>
                <div style={{
                  fontSize: '11px',
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginTop: '4px',
                }}>
                  Destinations
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatsBar;
